/**
 * Centralized message dispatcher for consistent timing and animations
 * Handles typing indicators and message delays in one place
 */

import type { Message } from '../types/index';

export interface DispatchConfig {
    typingDelay?: number; // Delay before showing typing indicator
    typingDuration?: number; // How long typing indicator shows
    messageDelay?: number; // Delay between messages
    sessionId?: string; // Unique ID for this dispatch session (for cancellation)
    checkCancelled?: () => boolean; // Function to check if dispatch should be cancelled
}

export interface MessageDispatcher {
    setIsTyping: (typing: boolean) => void;
    setMessages: (updater: (prev: Message[]) => Message[]) => void;
}

const DEFAULT_CONFIG: Required<Omit<DispatchConfig, 'sessionId' | 'checkCancelled'>> = {
    typingDelay: 500, // Small delay before typing starts
    typingDuration: 1200, // Typing indicator duration
    messageDelay: 800, // Delay between messages
};

/**
 * Dispatch a single message with typing animation
 */
export async function dispatchMessage(
    message: Message,
    dispatcher: MessageDispatcher,
    config: DispatchConfig = {}
): Promise<void> {
    const cfg = { ...DEFAULT_CONFIG, ...config };

    // Check if cancelled before showing typing
    if (config.checkCancelled && config.checkCancelled()) {
        return;
    }

    // Show typing indicator
    dispatcher.setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, cfg.typingDuration));

    // Check if cancelled after typing delay
    if (config.checkCancelled && config.checkCancelled()) {
        dispatcher.setIsTyping(false);
        return;
    }

    dispatcher.setIsTyping(false);

    // Add message
    dispatcher.setMessages(prev => [...prev, message]);

    // Check if cancelled before message delay
    if (config.checkCancelled && config.checkCancelled()) {
        return;
    }

    // Delay before next action
    await new Promise(resolve => setTimeout(resolve, cfg.messageDelay));
}

/**
 * Dispatch multiple messages sequentially with typing animations
 */
export async function dispatchMessages(
    messages: Message[],
    dispatcher: MessageDispatcher,
    config: DispatchConfig = {}
): Promise<void> {
    const cfg = { ...DEFAULT_CONFIG, ...config };

    for (let i = 0; i < messages.length; i++) {
        // Check if cancelled before each message
        if (config.checkCancelled && config.checkCancelled()) {
            return;
        }

        const message = messages[i];

        // First message has no typing delay
        if (i === 0) {
            dispatcher.setMessages(prev => [...prev, message]);
            await new Promise(resolve => setTimeout(resolve, cfg.messageDelay));
        } else {
            await dispatchMessage(message, dispatcher, config);
        }
    }
}

/**
 * Add user message immediately (no typing indicator needed)
 */
export function dispatchUserMessage(
    text: string,
    dispatcher: MessageDispatcher
): Message {
    const userMessage: Message = {
        type: 'regular',
        text,
        sender: 'user',
        timestamp: new Date(),
    };

    dispatcher.setMessages(prev => [...prev, userMessage]);
    return userMessage;
}
